import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';

const DatePicker = ({
 value = '',
 onChange = () => {},
 placeholder = 'Select date',
 minDate = null,
 maxDate = null,
 allowPastDates = true,
 disabled = false,
 required = false,
 label = '',
 error = '',
 className = '',
 size = 'md',
 ...props
}) => {
 const [isOpen, setIsOpen] = useState(false);
 const [currentMonth, setCurrentMonth] = useState(new Date());
 const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);
 const [hoveredDate, setHoveredDate] = useState(null);
 const [menuStyle, setMenuStyle] = useState(null);
 const datePickerRef = useRef(null);
 const triggerRef = useRef(null);
 const menuRef = useRef(null);

 const sizeClasses = {
 sm: 'h-7 min-h-7 px-2.5 text-xs leading-none',
 md: 'h-8 min-h-8 px-3 text-xs leading-none',
 lg: 'h-9 min-h-9 px-3 text-sm leading-none',
 };

 const baseClasses = 'box-border flex w-full items-center border border-border rounded-md shadow-none focus:outline-none focus:ring-1 focus:ring-ink focus:border-ink transition-colors duration-150 bg-surface cursor-pointer';

 const inputClasses = `
 ${baseClasses}
 ${sizeClasses[size] || sizeClasses.md}
 ${error ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''}
 ${disabled ? 'bg-surface-muted cursor-not-allowed opacity-60' : ''}
 ${className}
 `.trim();

 const updateMenuPosition = useCallback(() => {
 const trigger = triggerRef.current;
 if (!trigger) return;

 const rect = trigger.getBoundingClientRect();
 const gap = 4;
 const viewportPadding = 8;
 const maxMenuWidth = Math.max(160, window.innerWidth - viewportPadding * 2);
 const menuWidth = Math.min(Math.max(rect.width, Math.min(280, maxMenuWidth)), maxMenuWidth);
 const estimatedHeight = Math.min(360, window.innerHeight - viewportPadding * 2);
 const spaceBelow = window.innerHeight - rect.bottom - gap;
 const openUp = spaceBelow < estimatedHeight && rect.top > spaceBelow;

 let left = rect.left;
 if (left + menuWidth > window.innerWidth - viewportPadding) {
 left = Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding);
 }
 if (left < viewportPadding) left = viewportPadding;

 setMenuStyle({
 position: 'fixed',
 top: openUp ? undefined : rect.bottom + gap,
 bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
 left,
 width: menuWidth,
 maxWidth: maxMenuWidth,
 maxHeight: estimatedHeight,
 overflowY: 'auto',
 zIndex: 12050,
 });
 }, []);

 useLayoutEffect(() => {
 if (!isOpen) {
 setMenuStyle(null);
 return undefined;
 }
 updateMenuPosition();
 const onReposition = () => updateMenuPosition();
 window.addEventListener('resize', onReposition);
 window.addEventListener('scroll', onReposition, true);
 return () => {
 window.removeEventListener('resize', onReposition);
 window.removeEventListener('scroll', onReposition, true);
 };
 }, [isOpen, updateMenuPosition]);

 useEffect(() => {
 if (!isOpen) return undefined;
 const handleClickOutside = (event) => {
 const inTrigger = datePickerRef.current?.contains(event.target);
 const inMenu = menuRef.current?.contains(event.target);
 if (!inTrigger && !inMenu) {
 setIsOpen(false);
 }
 };
 document.addEventListener('mousedown', handleClickOutside);
 return () => document.removeEventListener('mousedown', handleClickOutside);
 }, [isOpen]);

 useEffect(() => {
 if (value) {
 setSelectedDate(new Date(value));
 } else {
 setSelectedDate(null);
 }
 }, [value]);

 const formatDisplayDate = (date) => {
 if (!date) return placeholder;
 return date.toLocaleDateString('en-US', {
 month: 'short',
 day: 'numeric',
 year: 'numeric'
 });
 };

 const getDaysInMonth = (date) => {
 const year = date.getFullYear();
 const month = date.getMonth();
 const firstDay = new Date(year, month, 1);
 const lastDay = new Date(year, month + 1, 0);
 const daysInMonth = lastDay.getDate();
 const startingDayOfWeek = firstDay.getDay();

 const days = [];

 for (let i = 0; i < startingDayOfWeek; i++) {
 days.push(null);
 }

 for (let day = 1; day <= daysInMonth; day++) {
 days.push(new Date(year, month, day));
 }

 return days;
 };

 const parseBoundaryDate = (value) => {
 if (!value) return null;
 if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
 const [y, m, d] = value.split('-').map(Number);
 return new Date(y, m - 1, d);
 }
 const parsed = new Date(value);
 if (Number.isNaN(parsed.getTime())) return null;
 parsed.setHours(0, 0, 0, 0);
 return parsed;
 };

 const isDateDisabled = (date) => {
 if (!date) return true;

 const today = new Date();
 today.setHours(0, 0, 0, 0);
 const candidate = new Date(date);
 candidate.setHours(0, 0, 0, 0);

 const min = parseBoundaryDate(minDate);
 const max = parseBoundaryDate(maxDate);
 if (min && candidate < min) return true;
 if (max && candidate > max) return true;
 if (!allowPastDates && candidate < today) return true;

 return false;
 };

 const isDateSelected = (date) => {
 if (!date || !selectedDate) return false;
 return date.toDateString() === selectedDate.toDateString();
 };

 const isDateHovered = (date) => {
 if (!date || !hoveredDate) return false;
 return date.toDateString() === hoveredDate.toDateString();
 };

 const handleDateSelect = (date) => {
 if (!date || isDateDisabled(date)) return;

 setSelectedDate(date);
 onChange(date.toISOString().split('T')[0]);
 setIsOpen(false);
 };

 const handleClear = (e) => {
 e.stopPropagation();
 setSelectedDate(null);
 onChange('');
 };

 const navigateMonth = (direction) => {
 setCurrentMonth((prev) => {
 const newMonth = new Date(prev);
 newMonth.setMonth(prev.getMonth() + direction);
 return newMonth;
 });
 };

 const goToToday = () => {
 const today = new Date();
 setCurrentMonth(today);
 if (!isDateDisabled(today)) {
 handleDateSelect(today);
 }
 };

 const days = getDaysInMonth(currentMonth);
 const monthNames = [
 'January', 'February', 'March', 'April', 'May', 'June',
 'July', 'August', 'September', 'October', 'November', 'December'
 ];
 const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

 const calendarMenu = isOpen && menuStyle
 ? createPortal(
 <div
 ref={menuRef}
 style={menuStyle}
 className="bg-surface border border-border rounded-xl shadow-sm"
 >
 <div className="flex items-center justify-between p-4 border-b border-border">
 <button
 type="button"
 onClick={() => navigateMonth(-1)}
 className="p-1 hover:bg-surface-muted rounded-md transition-colors"
 >
 <ChevronLeft className="w-4 h-4 text-ink-secondary" />
 </button>

 <div className="text-center">
 <h3 className="text-lg font-semibold text-ink">
 {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
 </h3>
 </div>

 <button
 type="button"
 onClick={() => navigateMonth(1)}
 className="p-1 hover:bg-surface-muted rounded-md transition-colors"
 >
 <ChevronRight className="w-4 h-4 text-ink-secondary" />
 </button>
 </div>

 <div className="p-4">
 <div className="grid grid-cols-7 gap-1 mb-2">
 {dayNames.map((day) => (
 <div key={day} className="text-center text-xs font-medium text-ink-tertiary py-2">
 {day}
 </div>
 ))}
 </div>

 <div className="grid grid-cols-7 gap-1">
 {days.map((day, index) => {
 if (!day) {
 return <div key={index} className="h-8" />;
 }

 const isDisabled = isDateDisabled(day);
 const isSelected = isDateSelected(day);
 const isHovered = isDateHovered(day);
 const isToday = day.toDateString() === new Date().toDateString();

 return (
 <button
 key={day.toISOString()}
 type="button"
 onClick={() => handleDateSelect(day)}
 onMouseEnter={() => setHoveredDate(day)}
 onMouseLeave={() => setHoveredDate(null)}
 disabled={isDisabled}
 className={`h-8 w-8 text-sm rounded-md transition-colors flex items-center justify-center ${isDisabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-surface-muted cursor-pointer' } ${isSelected ? 'bg-ink text-white hover:bg-black' : isHovered ? 'bg-surface-muted text-ink' : 'text-ink' } ${isToday && !isSelected ? 'bg-gray-100 font-semibold' : ''}`}
 >
 {day.getDate()}
 </button>
 );
 })}
 </div>

 <div className="mt-4 pt-3 border-t border-border">
 <button
 type="button"
 onClick={goToToday}
 disabled={isDateDisabled(new Date())}
 className="w-full text-sm text-ink hover:text-ink disabled:text-gray-400 disabled:cursor-not-allowed"
 >
 Go to Today
 </button>
 </div>
 </div>
 </div>,
 document.body
 )
 : null;

 return (
 <div className="w-full">
 {label && (
 <label className="mb-1.5 block text-xs font-medium text-ink sm:mb-2 sm:text-sm">
 {label}
 {required && <span className="text-red-500 ml-1">*</span>}
 </label>
 )}

 <div className="relative" ref={datePickerRef}>
 <div
 ref={triggerRef}
 className={inputClasses}
 onClick={() => !disabled && setIsOpen(!isOpen)}
 {...props}
 >
 <div className="flex h-full w-full items-center justify-between gap-2">
 <div className="flex min-w-0 items-center">
 <Calendar className="mr-2 h-3.5 w-3.5 shrink-0 text-ink-tertiary" />
 <span className={`truncate ${selectedDate ? 'text-ink' : 'text-ink-tertiary'}`}>
 {formatDisplayDate(selectedDate)}
 </span>
 </div>
 <div className="flex shrink-0 items-center gap-1.5">
 {selectedDate && (
 <button
 type="button"
 onClick={handleClear}
 className="text-ink-tertiary hover:text-ink"
 >
 <X className="h-3.5 w-3.5" />
 </button>
 )}
 <ChevronDown className={`h-3.5 w-3.5 text-ink-tertiary transition-transform ${isOpen ? 'rotate-180' : ''}`} />
 </div>
 </div>
 </div>

 {calendarMenu}
 </div>

 {error && (
 <p className="mt-1 text-xs text-red-600 sm:text-sm">{error}</p>
 )}
 </div>
 );
};

export default DatePicker;
